import React, { useState, useEffect, useCallback } from "react";
import { Button, Card, Typography, Space, notification, Alert } from "antd";
import { BellOutlined, SendOutlined } from "@ant-design/icons";
import {
  requestNotificationPermission,
  onMessageListener,
} from "../../config/firebase";
import { notificationsService } from "../../api/notifications";
import "./PushNotificationTest.css";

const { Title, Text } = Typography;

const PushNotificationTest = () => {
  const [token, setToken] = useState(null);
  const [permission, setPermission] = useState("default");
  const [isLoading, setIsLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState([]);
  const [savedToken, setSavedToken] = useState(
    localStorage.getItem("firebaseToken")
  );

  // Стили для кнопок (минимальная ширина)
  const buttonStyle = (minWidth) => ({
    minWidth: minWidth,
  });

  // Определяем, помещаются ли кнопки в одну строку
  const getButtonLayout = () => {
    const screenWidth = window.innerWidth;
    if (screenWidth >= 1200) {
      return "single-row"; // Все кнопки в одну строку
    } else if (screenWidth >= 768) {
      return "two-rows"; // Две строки
    } else {
      return "vertical"; // Вертикально
    }
  };

  const [buttonLayout, setButtonLayout] = useState(getButtonLayout());

  // Слушаем изменения размера окна
  useEffect(() => {
    const handleResize = () => {
      setButtonLayout(getButtonLayout());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const addDebugInfo = (message, type = "info") => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugInfo((prev) => [...prev, { message, type, timestamp }]);
  };

  const checkNotificationPermission = useCallback(() => {
    const currentPermission = Notification.permission;
    setPermission(currentPermission);
    addDebugInfo(`Текущее разрешение: ${currentPermission}`, "info");
    return currentPermission;
  }, []);

  const requestPermission = async () => {
    setIsLoading(true);
    try {
      addDebugInfo("Запрашиваем разрешение на уведомления...", "info");

      const newToken = await requestNotificationPermission();
      if (newToken) {
        setToken(newToken);
        setSavedToken(newToken);
        addDebugInfo(
          `Получен токен: ${newToken.substring(0, 50)}...`,
          "success"
        );
        checkNotificationPermission();
      } else {
        addDebugInfo("Токен не получен", "error");
      }
    } catch (error) {
      addDebugInfo(`Ошибка при запросе разрешения: ${error.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const testForegroundMessage = async () => {
    try {
      addDebugInfo("Тестируем получение foreground сообщений...", "info");

      // Показываем тестовое уведомление в интерфейсе
      notification.success({
        message: "Foreground уведомление",
        description: "Тестовое сообщение для активного приложения",
        placement: "topRight",
        duration: 4,
      });

      addDebugInfo("Foreground уведомление показано в интерфейсе", "success");

      // Также устанавливаем слушатель для реальных сообщений
      onMessageListener()
        .then((payload) => {
          addDebugInfo(
            `Получено реальное foreground сообщение: ${JSON.stringify(
              payload
            )}`,
            "success"
          );
          notification.info({
            message: "Реальное уведомление",
            description: "Получено реальное push-уведомление",
            placement: "topRight",
          });
        })
        .catch((error) => {
          addDebugInfo(
            `Ошибка foreground сообщения: ${error.message}`,
            "error"
          );
        });
    } catch (error) {
      addDebugInfo(`Ошибка тестирования: ${error.message}`, "error");
    }
  };

  const testBackendNotification = async () => {
    try {
      addDebugInfo("Отправляем тестовое уведомление через бэкенд...", "info");

      // Здесь должен быть вызов вашего API для отправки тестового уведомления
      // Например: await notificationsService.sendTestNotification();

      addDebugInfo("Тестовое уведомление отправлено на бэкенд", "info");
      notification.info({
        message: "Тест отправлен",
        description: "Проверьте, пришло ли push-уведомление",
        placement: "topRight",
      });
    } catch (error) {
      addDebugInfo(`Ошибка отправки теста: ${error.message}`, "error");
    }
  };

  const testTokenRegistration = async () => {
    try {
      const currentToken = localStorage.getItem("firebaseToken");
      if (!currentToken) {
        addDebugInfo("Токен не найден в localStorage", "error");
        return;
      }

      addDebugInfo("Тестируем регистрацию токена на бэкенде...", "info");
      addDebugInfo(`Токен: ${currentToken.substring(0, 50)}...`, "info");

      const result = await notificationsService.registerToken(currentToken);
      addDebugInfo("Токен успешно зарегистрирован на бэкенде!", "success");
      addDebugInfo(`Ответ сервера: ${JSON.stringify(result)}`, "success");

      notification.success({
        message: "Токен зарегистрирован",
        description: "Токен успешно отправлен на бэкенд",
        placement: "topRight",
      });
    } catch (error) {
      addDebugInfo(`Ошибка регистрации токена: ${error.message}`, "error");
      notification.error({
        message: "Ошибка регистрации",
        description: error.message,
        placement: "topRight",
      });
    }
  };

  const forceRegisterServiceWorker = async () => {
    try {
      addDebugInfo("Принудительная регистрация Service Worker...", "info");

      // Удаляем все старые регистрации
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (let registration of registrations) {
        if (
          registration.scope.includes("/") &&
          (registration.active?.scriptURL?.includes("firebase-messaging-sw") ||
            registration.installing?.scriptURL?.includes(
              "firebase-messaging-sw"
            ) ||
            registration.waiting?.scriptURL?.includes("firebase-messaging-sw"))
        ) {
          await registration.unregister();
          addDebugInfo("Старый Service Worker удален", "info");
        }
      }

      // Регистрируем новый
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js"
      );
      addDebugInfo("Service Worker успешно зарегистрирован", "success");
      addDebugInfo(
        `Service Worker активен: ${registration.active ? "Да" : "Нет"}`,
        "info"
      );

      notification.success({
        message: "Service Worker зарегистрирован",
        description: "Service Worker успешно перерегистрирован",
        placement: "topRight",
      });
    } catch (error) {
      addDebugInfo(
        `Ошибка принудительной регистрации: ${error.message}`,
        "error"
      );
      notification.error({
        message: "Ошибка регистрации",
        description: error.message,
        placement: "topRight",
      });
    }
  };

  const testBrowserNotification = async () => {
    try {
      addDebugInfo("Тестируем браузерное уведомление...", "info");

      // Проверяем разрешение
      if (Notification.permission !== "granted") {
        addDebugInfo("Разрешение на уведомления не предоставлено", "error");
        notification.error({
          message: "Нет разрешения",
          description: "Сначала запросите разрешение на уведомления",
          placement: "topRight",
        });
        return;
      }

      // Создаем браузерное уведомление (без actions - они не поддерживаются в new Notification())
      const browserNotification = new Notification(
        "Тест браузерного уведомления",
        {
          body: "Это тестовое уведомление для проверки работы браузерных уведомлений",
          icon: "/logo.png",
          badge: "/logo.png",
          tag: "test-notification",
          requireInteraction: false,
          silent: false,
          vibrate: [200, 100, 200],
        }
      );

      addDebugInfo("Браузерное уведомление создано", "success");

      // Обработка клика по уведомлению
      browserNotification.onclick = () => {
        addDebugInfo("Браузерное уведомление кликнуто", "info");
        window.focus();
        browserNotification.close();
      };

      // Автоматически закрываем через 5 секунд
      setTimeout(() => {
        browserNotification.close();
        addDebugInfo("Браузерное уведомление автоматически закрыто", "info");
      }, 5000);

      notification.success({
        message: "Браузерное уведомление отправлено",
        description: "Проверьте, появилось ли нативное уведомление",
        placement: "topRight",
      });
    } catch (error) {
      addDebugInfo(
        `Ошибка создания браузерного уведомления: ${error.message}`,
        "error"
      );
      notification.error({
        message: "Ошибка уведомления",
        description: error.message,
        placement: "topRight",
      });
    }
  };

  const testServiceWorkerNotification = async () => {
    try {
      addDebugInfo("Тестируем уведомление через Service Worker...", "info");

      // Проверяем разрешение
      if (Notification.permission !== "granted") {
        addDebugInfo("Разрешение на уведомления не предоставлено", "error");
        notification.error({
          message: "Нет разрешения",
          description: "Сначала запросите разрешение на уведомления",
          placement: "topRight",
        });
        return;
      }

      // Получаем регистрацию Service Worker
      const registration = await navigator.serviceWorker.getRegistration(
        "/firebase-messaging-sw.js"
      );
      if (!registration) {
        addDebugInfo("Service Worker не найден", "error");
        notification.error({
          message: "Service Worker не найден",
          description: "Сначала зарегистрируйте Service Worker",
          placement: "topRight",
        });
        return;
      }

      // Показываем уведомление через Service Worker (с поддержкой actions)
      await registration.showNotification("Тест SW уведомления", {
        body: "Это уведомление показано через Service Worker с кнопками действий",
        icon: "/logo.png",
        badge: "/logo.png",
        tag: "sw-test-notification",
        requireInteraction: false,
        silent: false,
        vibrate: [200, 100, 200],
        actions: [
          {
            action: "open",
            title: "Открыть",
            icon: "/logo.png",
          },
          {
            action: "close",
            title: "Закрыть",
          },
        ],
      });

      addDebugInfo("Service Worker уведомление создано с actions", "success");

      notification.success({
        message: "SW уведомление отправлено",
        description: "Проверьте, появилось ли уведомление с кнопками",
        placement: "topRight",
      });
    } catch (error) {
      addDebugInfo(`Ошибка создания SW уведомления: ${error.message}`, "error");
      notification.error({
        message: "Ошибка SW уведомления",
        description: error.message,
        placement: "topRight",
      });
    }
  };

  const checkServiceWorker = useCallback(() => {
    if ("serviceWorker" in navigator) {
      // Проверяем все зарегистрированные Service Worker
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) => {
          addDebugInfo(
            `Найдено Service Worker: ${registrations.length}`,
            "info"
          );

          // Ищем Firebase messaging Service Worker
          const firebaseSW = registrations.find(
            (reg) =>
              reg.scope.includes("/") &&
              (reg.active?.scriptURL?.includes("firebase-messaging-sw") ||
                reg.installing?.scriptURL?.includes("firebase-messaging-sw") ||
                reg.waiting?.scriptURL?.includes("firebase-messaging-sw"))
          );

          if (firebaseSW) {
            addDebugInfo("Firebase Service Worker найден", "success");
            addDebugInfo(
              `Service Worker активен: ${firebaseSW.active ? "Да" : "Нет"}`,
              "info"
            );
            addDebugInfo(
              `Service Worker состояние: ${
                firebaseSW.active?.state ||
                firebaseSW.installing?.state ||
                firebaseSW.waiting?.state
              }`,
              "info"
            );
          } else {
            addDebugInfo("Firebase Service Worker не найден", "error");
            addDebugInfo("Попытка регистрации Service Worker...", "info");

            // Пытаемся зарегистрировать Service Worker
            navigator.serviceWorker
              .register("/firebase-messaging-sw.js")
              .then((registration) => {
                addDebugInfo(
                  "Service Worker успешно зарегистрирован",
                  "success"
                );
                addDebugInfo(
                  `Service Worker активен: ${
                    registration.active ? "Да" : "Нет"
                  }`,
                  "info"
                );
              })
              .catch((error) => {
                addDebugInfo(
                  `Ошибка регистрации Service Worker: ${error.message}`,
                  "error"
                );
              });
          }
        })
        .catch((error) => {
          addDebugInfo(
            `Ошибка получения регистраций: ${error.message}`,
            "error"
          );
        });
    } else {
      addDebugInfo("Service Worker не поддерживается браузером", "error");
    }
  }, []);

  useEffect(() => {
    checkNotificationPermission();
    checkServiceWorker();

    // Проверяем сохраненный токен
    const savedToken = localStorage.getItem("firebaseToken");
    if (savedToken) {
      addDebugInfo("Найден сохраненный токен в localStorage", "success");
      addDebugInfo(`Токен: ${savedToken.substring(0, 50)}...`, "info");
    } else {
      addDebugInfo("Сохраненный токен не найден", "warning");
    }

    // Слушаем сообщения от service worker
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", (event) => {
        addDebugInfo(
          `Сообщение от Service Worker: ${JSON.stringify(event.data)}`,
          "success"
        );
      });
    }
  }, [checkNotificationPermission, checkServiceWorker]);

  return (
    <Card title="Тест Push-уведомлений" className="push-notification-test">
      <Space direction="vertical" style={{ width: "100%" }}>
        <Alert
          message={`Разрешение на уведомления: ${permission}`}
          type={
            permission === "granted"
              ? "success"
              : permission === "denied"
              ? "error"
              : "warning"
          }
          showIcon
        />

        <div style={{ width: "100%" }}>
          {buttonLayout === "single-row" ? (
            // Все кнопки в одну строку для широких экранов
            <div className="button-container">
              <Button
                type="primary"
                icon={<BellOutlined />}
                onClick={requestPermission}
                loading={isLoading}
                disabled={permission === "granted"}
                style={buttonStyle("140px")}
              >
                Запросить разрешение
              </Button>

              <Button
                icon={<SendOutlined />}
                onClick={testForegroundMessage}
                disabled={permission !== "granted"}
                style={buttonStyle("120px")}
              >
                Тест Foreground
              </Button>

              <Button
                icon={<SendOutlined />}
                onClick={testBackendNotification}
                disabled={permission !== "granted"}
                style={buttonStyle("120px")}
              >
                Тест Backend
              </Button>

              <Button
                icon={<SendOutlined />}
                onClick={testTokenRegistration}
                disabled={!localStorage.getItem("firebaseToken")}
                style={buttonStyle("160px")}
              >
                Тест регистрации токена
              </Button>

              <Button
                icon={<BellOutlined />}
                onClick={forceRegisterServiceWorker}
                type="dashed"
                style={buttonStyle("140px")}
              >
                Перерегистрировать SW
              </Button>

              <Button
                icon={<BellOutlined />}
                onClick={testBrowserNotification}
                type="default"
                style={{
                  ...buttonStyle("180px"),
                  backgroundColor: "#52c41a",
                  borderColor: "#52c41a",
                  color: "white",
                }}
              >
                Тест браузерного уведомления
              </Button>

              <Button
                icon={<BellOutlined />}
                onClick={testServiceWorkerNotification}
                type="default"
                style={{
                  ...buttonStyle("200px"),
                  backgroundColor: "#1890ff",
                  borderColor: "#1890ff",
                  color: "white",
                }}
              >
                Тест SW уведомления (с кнопками)
              </Button>
            </div>
          ) : buttonLayout === "two-rows" ? (
            // Две строки для средних экранов
            <>
              <div className="button-container">
                <Button
                  type="primary"
                  icon={<BellOutlined />}
                  onClick={requestPermission}
                  loading={isLoading}
                  disabled={permission === "granted"}
                  style={buttonStyle("140px")}
                >
                  Запросить разрешение
                </Button>

                <Button
                  icon={<SendOutlined />}
                  onClick={testForegroundMessage}
                  disabled={permission !== "granted"}
                  style={buttonStyle("120px")}
                >
                  Тест Foreground
                </Button>

                <Button
                  icon={<SendOutlined />}
                  onClick={testBackendNotification}
                  disabled={permission !== "granted"}
                  style={buttonStyle("120px")}
                >
                  Тест Backend
                </Button>
              </div>

              <div className="button-container">
                <Button
                  icon={<SendOutlined />}
                  onClick={testTokenRegistration}
                  disabled={!localStorage.getItem("firebaseToken")}
                  style={buttonStyle("160px")}
                >
                  Тест регистрации токена
                </Button>

                <Button
                  icon={<BellOutlined />}
                  onClick={forceRegisterServiceWorker}
                  type="dashed"
                  style={buttonStyle("140px")}
                >
                  Перерегистрировать SW
                </Button>

                <Button
                  icon={<BellOutlined />}
                  onClick={testBrowserNotification}
                  type="default"
                  style={{
                    ...buttonStyle("180px"),
                    backgroundColor: "#52c41a",
                    borderColor: "#52c41a",
                    color: "white",
                  }}
                >
                  Тест браузерного уведомления
                </Button>

                <Button
                  icon={<BellOutlined />}
                  onClick={testServiceWorkerNotification}
                  type="default"
                  style={{
                    ...buttonStyle("200px"),
                    backgroundColor: "#1890ff",
                    borderColor: "#1890ff",
                    color: "white",
                  }}
                >
                  Тест SW уведомления (с кнопками)
                </Button>
              </div>
            </>
          ) : (
            // Вертикально для узких экранов
            <>
              <div className="button-container vertical">
                <Button
                  type="primary"
                  icon={<BellOutlined />}
                  onClick={requestPermission}
                  loading={isLoading}
                  disabled={permission === "granted"}
                  style={buttonStyle("140px")}
                >
                  Запросить разрешение
                </Button>

                <Button
                  icon={<SendOutlined />}
                  onClick={testForegroundMessage}
                  disabled={permission !== "granted"}
                  style={buttonStyle("120px")}
                >
                  Тест Foreground
                </Button>

                <Button
                  icon={<SendOutlined />}
                  onClick={testBackendNotification}
                  disabled={permission !== "granted"}
                  style={buttonStyle("120px")}
                >
                  Тест Backend
                </Button>
              </div>

              <div className="button-container vertical">
                <Button
                  icon={<SendOutlined />}
                  onClick={testTokenRegistration}
                  disabled={!localStorage.getItem("firebaseToken")}
                  style={buttonStyle("160px")}
                >
                  Тест регистрации токена
                </Button>

                <Button
                  icon={<BellOutlined />}
                  onClick={forceRegisterServiceWorker}
                  type="dashed"
                  style={buttonStyle("140px")}
                >
                  Перерегистрировать SW
                </Button>
              </div>

              <div className="button-container vertical">
                <Button
                  icon={<BellOutlined />}
                  onClick={testBrowserNotification}
                  type="default"
                  style={{
                    ...buttonStyle("180px"),
                    backgroundColor: "#52c41a",
                    borderColor: "#52c41a",
                    color: "white",
                  }}
                >
                  Тест браузерного уведомления
                </Button>

                <Button
                  icon={<BellOutlined />}
                  onClick={testServiceWorkerNotification}
                  type="default"
                  style={{
                    ...buttonStyle("200px"),
                    backgroundColor: "#1890ff",
                    borderColor: "#1890ff",
                    color: "white",
                  }}
                >
                  Тест SW уведомления (с кнопками)
                </Button>
              </div>
            </>
          )}
        </div>

        {token && (
          <Alert
            message="Firebase токен получен"
            description={`${token.substring(0, 50)}...`}
            type="success"
            showIcon
          />
        )}

        {savedToken && (
          <Alert
            message="Сохраненный токен (для бэкендера)"
            description={
              <div>
                <Text copyable={{ text: savedToken }}>
                  {savedToken.substring(0, 50)}...
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  Скопируйте этот токен и передайте бэкендеру для настройки
                </Text>
              </div>
            }
            type="warning"
            showIcon
          />
        )}

        <Title level={5}>Отладочная информация:</Title>
        <div className="debug-info">
          {debugInfo.map((info, index) => (
            <div key={index} style={{ marginBottom: 4 }}>
              <Text type="secondary">[{info.timestamp}]</Text>{" "}
              <Text
                type={
                  info.type === "error"
                    ? "danger"
                    : info.type === "success"
                    ? "success"
                    : "secondary"
                }
              >
                {info.message}
              </Text>
            </div>
          ))}
        </div>
      </Space>
    </Card>
  );
};

export default PushNotificationTest;
